import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import * as XLSX from 'xlsx';

const XLS_PATTERN = /\.xls(?:x)?$/i;

interface LearnerProps {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

@Component({
  selector: 'vnm-learners-import',
  templateUrl: './learners-import.component.html',
  styleUrls: ['./learners-import.component.scss']
})
export class LearnersImportComponent {
  @Input() importObjects = 'learners';

  @Output() imported = new EventEmitter<LearnerProps[]>();

  learners = [];

  constructor(private messageService: MessageService, private translateService: TranslateService) {}

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.stopPropagation();
    event.preventDefault();

    const { 0: file, length } = event.dataTransfer.files;

    if (length === 1 && file) {
      this.importFile(file);
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  onBrowse(_: Event, fileInput: HTMLInputElement) {
    fileInput.click();
  }

  onFileSelect(event: Event) {
    const file = (event.currentTarget as HTMLInputElement).files[0];
    if (file) {
      this.importFile(file);
    }
  }

  private detectPropertiesFromHeaders(item): LearnerProps {
    const keys = Object.keys(item);
    return {
      firstName: keys.find((key) => /first/i.test(key)),
      lastName: keys.find((key) => /last/i.test(key)),
      email: keys.find((key) => /email/i.test(key)),
      mobileNumber: keys.find((key) => /mobile|phone/i.test(key))
    };
  }

  private convertItemToImportPayload(item, props: LearnerProps): LearnerProps {
    return {
      firstName: item[props.firstName] ? String(item[props.firstName]) : '',
      lastName: item[props.lastName] ? String(item[props.lastName]) : '',
      email: item[props.email] ? String(item[props.email]).trim() : '',
      mobileNumber: item[props.mobileNumber] ? String(item[props.mobileNumber]).trim() : ''
    };
  }

  private importFile(file: File) {
    const { name } = file;
    if (!XLS_PATTERN.test(name)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('learners-import.import-wrong-type', { name })
      });
      return;
    }

    file.arrayBuffer().then((data) => {
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const items = XLSX.utils.sheet_to_json(sheet);
      const props = this.detectPropertiesFromHeaders(items[0]);

      // We want at least learners with names
      if (!props.firstName && !props.lastName) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('learners-import.missing-headers')
        });
        return;
      }

      this.learners = items.map((item) => this.convertItemToImportPayload(item, props));

      const importObjects = this.translateService.instant(`learners-import.importing.${this.importObjects}`);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('learners-import.import-success', {
          importObjects,
          count: this.learners.length
        })
      });

      this.imported.emit(this.learners);
    });
  }
}
